import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { ArrowLeft, CheckCircle2, MessageSquare, Activity, TrendingUp, MapPin, Zap, Target, Brain, Shield } from 'lucide-react';
import PreSignDialog from '../PreSign/PreSignDialog';

const SmartInsights = () => {
    const navigate = useNavigate();
    const [showPreSign, setShowPreSign] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        try {
            ReactGA.send({ hitType: 'pageview', page: '/products/smart-insights', title: 'Smart Insights' });
        } catch { }
    }, []);

    const analysisFeatures = [
        {
            title: 'Heatmaps and Occupancy',
            description: 'Visualizes player movement patterns and court coverage to identify positioning strengths and weaknesses.',
            icon: MapPin,
            color: 'from-blue-500 to-blue-600'
        },
        {
            title: 'Tracking Trails',
            description: 'Provides real-time player tracking data to analyze agility and court speed.',
            icon: TrendingUp,
            color: 'from-purple-500 to-purple-600'
        },
        {
            title: 'Rally Breakdowns',
            description: 'Offers rally-by-rally analysis, including rally length comparisons and phase-wise performance insights.',
            icon: Target,
            color: 'from-emerald to-emerald/80'
        }
    ];

    const conversationalFeatures = [
        { question: 'What was the win percentage in each phase?', icon: TrendingUp },
        { question: 'Which was the longest rally?', icon: Zap },
        { question: 'Show me all winning shots', icon: Target },
        { question: 'Where did I make the most errors?', icon: MapPin }
    ];

    const injuryFeatures = [
        {
            title: 'Data-Backed Rehab',
            description: 'Monitor recovery and address the critical pain point where players lose rankings due to poorly managed injury rehabilitation.',
            icon: Activity
        },
        {
            title: 'Scientific Interventions',
            description: 'Provides proof of improvement and safe recovery, ensuring athletes return to competition safely and effectively.',
            icon: Shield
        }
    ];

    return (
        <div className="min-h-screen bg-white">
            {/* Hero Section */}
            <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden bg-deep-charcoal">
                <div className="absolute inset-0 bg-gradient-to-br from-deep-charcoal via-gray-900 to-deep-charcoal"></div>
                <div className="absolute top-0 right-0 w-96 h-96 bg-emerald/10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald/5 rounded-full blur-3xl"></div>

                <div className="container mx-auto px-6 py-20 relative z-10">
                    <motion.button
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5 }}
                        onClick={() => navigate('/products')}
                        className="flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors group"
                    >
                        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                        <span className="font-semibold">Back to Products</span>
                    </motion.button>

                    <div className="max-w-5xl mx-auto text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                        >
                            <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight uppercase text-transparent bg-clip-text bg-gradient-to-r from-emerald to-neon-lime">
                                Smart Insights
                            </h1>
                        </motion.div>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="text-xl md:text-2xl text-gray-300 font-light leading-relaxed max-w-4xl mx-auto mb-8"
                        >
                            AI-powered match intelligence platform that transforms raw match footage into actionable, data-driven performance analytics
                        </motion.p>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                            className="text-lg text-gray-400 max-w-3xl mx-auto mb-12"
                        >
                            Elite-level metrics previously unavailable at the grassroots level
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.8 }}
                        >
                            <button
                                onClick={() => setShowPreSign(true)}
                                className="px-12 py-4 bg-gradient-to-r from-emerald to-emerald/80 text-white font-bold text-lg rounded-lg hover:shadow-glow-blue transition-all duration-300 hover:scale-105"
                            >
                                Try a Demo
                            </button>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Deep AI Video Analysis Section */}
            <section className="relative py-24 bg-gradient-to-b from-white via-gray-50 to-white overflow-hidden">
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute top-20 left-10 w-72 h-72 bg-emerald/5 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald/5 rounded-full blur-3xl"></div>
                </div>

                <div className="container mx-auto px-6 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <p className="text-sm md:text-base font-bold uppercase tracking-widest mb-3" style={{ color: '#247E45' }}>
                            Advanced Analytics
                        </p>
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 uppercase mb-4">
                            Deep AI <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald to-neon-lime">Video Analysis</span>
                        </h2>
                        <p className="text-gray-600 text-lg md:text-xl max-w-3xl mx-auto">
                            Specialized AI agents extract complex spatial and temporal data from smartphone-captured videos
                        </p>
                    </motion.div>

                    {/* Analysis Image */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="mb-16 max-w-6xl mx-auto"
                    >
                        <img
                            src="/assets/Video Analysis.png"
                            alt="Smart Insights Deep AI Video Analysis - Heatmaps, Tracking, and Rally Breakdowns"
                            className="w-full h-auto rounded-2xl shadow-2xl border-2 border-gray-200"
                        />
                    </motion.div>

                    {/* Analysis Features */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
                        {analysisFeatures.map((feature, index) => {
                            const Icon = feature.icon;
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-emerald hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                                >
                                    <div className={`w-16 h-16 rounded-xl flex items-center justify-center mb-6 bg-gradient-to-br ${feature.color}`}>
                                        <Icon className="w-8 h-8 text-white" />
                                    </div>
                                    <h3 className="text-2xl font-black text-gray-900 mb-4">{feature.title}</h3>
                                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Conversational Intelligence Section */}
            <section className="relative py-24 bg-deep-charcoal overflow-hidden">
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute top-1/4 left-0 w-96 h-96 bg-emerald/10 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-emerald/10 rounded-full blur-3xl"></div>
                </div>

                <div className="container mx-auto px-6 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <p className="text-emerald text-sm md:text-base font-bold uppercase tracking-widest mb-3">
                            Interactive Intelligence
                        </p>
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white uppercase mb-4">
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald to-neon-lime">Ask VISIST</span>
                        </h2>
                        <p className="text-gray-300 text-lg md:text-xl max-w-3xl mx-auto">
                            Natural language queries with instant visual answers backed by metrics and video clips
                        </p>
                    </motion.div>

                    {/* Conversational Image */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="mb-16 max-w-6xl mx-auto"
                    >
                        <img
                            src="/assets/Ask VISIST.png"
                            alt="Ask VISIST - Natural Language Queries with Instant Visual Answers"
                            className="w-full h-auto rounded-2xl shadow-2xl border-2 border-gray-700"
                        />
                    </motion.div>

                    {/* Example Questions */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
                        {conversationalFeatures.map((item, index) => {
                            const Icon = item.icon;
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    className="bg-white/5 backdrop-blur-sm border-2 border-white/10 rounded-xl p-6 hover:border-emerald hover:shadow-2xl transition-all duration-300 group"
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 bg-gradient-to-br from-emerald/20 to-emerald/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                                            <Icon className="w-6 h-6 text-emerald" />
                                        </div>
                                        <div className="flex-1">
                                            <MessageSquare className="w-5 h-5 text-emerald mb-2" />
                                            <p className="text-white font-semibold text-lg">{item.question}</p>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>

                    {/* Features */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className="mt-16 max-w-4xl mx-auto"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="bg-white/5 backdrop-blur-sm border-2 border-white/10 rounded-2xl p-8">
                                <Brain className="w-12 h-12 text-emerald mb-4" />
                                <h3 className="text-2xl font-black text-white mb-3">Natural Language Queries</h3>
                                <p className="text-gray-300 leading-relaxed">
                                    Ask specific questions in plain English and get instant, data-backed answers
                                </p>
                            </div>
                            <div className="bg-white/5 backdrop-blur-sm border-2 border-white/10 rounded-2xl p-8">
                                <Zap className="w-12 h-12 text-emerald mb-4" />
                                <h3 className="text-2xl font-black text-white mb-3">Instant Visual Answers</h3>
                                <p className="text-gray-300 leading-relaxed">
                                    Receive immediate answers with metrics and corresponding video clips
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Injury Risk Monitoring Section */}
            <section className="relative py-24 bg-white overflow-hidden">
                <div className="container mx-auto px-6 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <p className="text-sm md:text-base font-bold uppercase tracking-widest mb-3 text-emerald">
                            Coming Soon
                        </p>
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 uppercase mb-4">
                            Injury Risk & <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald to-neon-lime">Recovery Monitoring</span>
                        </h2>
                        <p className="text-gray-600 text-lg md:text-xl max-w-3xl mx-auto">
                            Beyond tactical performance—addressing athlete longevity through injury-risk signals and predictions
                        </p>
                    </motion.div>

                    {/* Injury Image */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="mb-16 max-w-6xl mx-auto"
                    >
                        <img
                            src="/assets/Injury.png"
                            alt="Injury Risk and Recovery Monitoring Dashboard"
                            className="w-full h-auto rounded-2xl shadow-2xl border-2 border-gray-200"
                        />
                    </motion.div>

                    {/* Injury Features */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-6xl mx-auto">
                        {injuryFeatures.map((feature, index) => {
                            const Icon = feature.icon;
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, x: index === 0 ? -30 : 30 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: 0.2 }}
                                    className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300"
                                >
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-emerald/10">
                                            <Icon className="w-6 h-6 text-emerald" />
                                        </div>
                                        <h3 className="text-2xl font-black text-gray-900">{feature.title}</h3>
                                    </div>
                                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                                </motion.div>
                            );
                        })}
                    </div>

                    {/* Key Benefits */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className="mt-16 max-w-4xl mx-auto bg-gradient-to-br from-emerald/10 to-white border-2 border-emerald/30 rounded-2xl p-8 md:p-10"
                    >
                        <div className="flex items-start gap-4 mb-6">
                            <CheckCircle2 className="w-8 h-8 text-emerald flex-shrink-0 mt-1" />
                            <div>
                                <h4 className="text-xl font-black text-gray-900 mb-2">Critical Pain Point Addressed</h4>
                                <p className="text-gray-700 leading-relaxed">
                                    Players often lose rankings due to poorly managed injury rehabilitation. Smart Insights helps coaches and parents monitor recovery with data-backed insights.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <CheckCircle2 className="w-8 h-8 text-emerald flex-shrink-0 mt-1" />
                            <div>
                                <h4 className="text-xl font-black text-gray-900 mb-2">Safe Return to Competition</h4>
                                <p className="text-gray-700 leading-relaxed">
                                    Provides proof of improvement and ensures athletes return to competition safely and effectively.
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="relative py-20 bg-deep-charcoal overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-deep-charcoal via-gray-900 to-deep-charcoal"></div>
                <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>

                <div className="container mx-auto px-6 relative z-10 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
                            Ready to Unlock Match Intelligence?
                        </h2>
                        <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                            Transform your match footage into actionable insights with AI-powered analytics
                        </p>
                        <button
                            onClick={() => setShowPreSign(true)}
                            className="px-12 py-4 bg-gradient-to-r from-emerald to-emerald/80 text-white font-bold text-lg rounded-lg hover:shadow-glow-blue transition-all duration-300 hover:scale-105"
                        >
                            Try a Demo
                        </button>
                    </motion.div>
                </div>
            </section>

            {/* PreSign Dialog */}
            <PreSignDialog
                open={showPreSign}
                onClose={() => setShowPreSign(false)}
            />
        </div>
    );
};

export default SmartInsights;
